import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { AnimatedVoiceOrb } from "./AnimatedVoiceOrb";
import { useVapiDemo, DemoConfig, CallStatus } from "@/hooks/use-vapi-demo";
import { Link } from "react-router-dom";
import { AIIndicatorChip, TrustSignals } from "./honest-demo";

interface VoiceCallInterfaceProps {
  sessionId: string;
  config: DemoConfig;
  onComplete: () => void;
  onBack: () => void;
}

export function VoiceCallInterface({
  sessionId,
  config,
  onComplete,
  onBack,
}: VoiceCallInterfaceProps) {
  const [showTranscript, setShowTranscript] = useState(false);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  const {
    status,
    duration,
    transcript,
    isMuted,
    volumeLevel,
    error,
    remainingDemos,
    isRateLimited,
    startDemo,
    endDemo,
    toggleMute,
    resetDemo,
    isSupported,
  } = useVapiDemo();

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript]);

  // Start the demo when component mounts
  useEffect(() => {
    if (status === "idle") {
      startDemo(config, sessionId);
    }
  }, []);

  // Format duration as MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get status text
  const getStatusText = (callStatus: CallStatus) => {
    switch (callStatus) {
      case "idle":
        return "Ready";
      case "requesting-mic":
        return "Requesting microphone access...";
      case "generating":
        return `Creating AI for ${config.businessName}...`;
      case "connecting":
        return "Connecting to AI...";
      case "active":
        return "You're speaking with the AI receptionist";
      case "ending":
        return "Ending call...";
      case "ended":
        return "Call complete";
      case "error":
        return "Error occurred";
      default:
        return "";
    }
  };

  // Rate limited view
  if (isRateLimited) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl mx-auto text-center py-12"
      >
        <div className="bg-gradient-to-br from-[#2e2e2e] via-[#2d2d2d] to-[#2c2c2c] border border-white/[0.1] p-8">
          <div className="w-16 h-16 bg-[#333333] border border-white/[0.08] flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-100 mb-3">Demo Limit Reached</h2>
          <p className="text-gray-400 mb-6">
            You've used all 3 demos for today. Come back tomorrow for more, or book a
            personalized demo with our team right now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-gradient-to-r from-[#f4f4f4] to-lime-500 text-[#292929] font-bold hover:opacity-90 shadow-lg shadow-lime-500/20" size="lg" asChild>
              <Link to="/request-demo">Book a Discovery Call</Link>
            </Button>
            <Button variant="outline" onClick={onBack} className="border-white/[0.15] hover:bg-white/[0.05] text-gray-400">
              Go Back
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Error view
  if (status === "error" && error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl mx-auto text-center py-12"
      >
        <div className="bg-gradient-to-br from-[#2e2e2e] via-[#2d2d2d] to-[#2c2c2c] border border-white/[0.1] p-8">
          <div className="w-16 h-16 bg-[#333333] border border-white/[0.08] flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-100 mb-3">Something Went Wrong</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-gradient-to-r from-[#f4f4f4] to-lime-400 text-[#292929] font-bold hover:opacity-90 shadow-lg shadow-lime-500/20" size="lg" onClick={() => {
              resetDemo();
              startDemo(config, sessionId);
            }}>
              Try Again
            </Button>
            <Button variant="outline" onClick={onBack} className="border-white/[0.15] hover:bg-white/[0.05] text-gray-400">
              Go Back
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Call ended view
  if (status === "ended") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-xl mx-auto text-center py-12"
      >
        <div className="bg-gradient-to-br from-[#2e2e2e] via-[#2d2d2d] to-[#2c2c2c] border border-white/[0.1] p-8">
          <div className="w-20 h-20 bg-gradient-to-br from-lime-400 to-lime-600 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-lime-500/20">
            <CheckCircle className="w-10 h-10 text-[#292929]" />
          </div>
          <h2 className="text-3xl font-bold text-gray-100 mb-2">Demo Complete!</h2>
          <p className="text-xl text-gray-400 mb-2">
            Call duration: {formatDuration(duration)}
          </p>
          <p className="text-gray-400 mb-8">
            You just experienced how Myna's AI receptionist can handle calls for{" "}
            <span className="font-semibold text-gray-100">{config.businessName}</span>.
          </p>

          {/* Transcript summary */}
          {transcript.length > 0 && (
            <div className="mb-8">
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="flex items-center gap-2 mx-auto text-sm text-lime-400 hover:text-lime-300"
              >
                {showTranscript ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Hide Transcript
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    View Transcript ({transcript.filter((t) => t.isFinal).length} messages)
                  </>
                )}
              </button>

              <AnimatePresence>
                {showTranscript && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-4 bg-[#333333] border border-white/[0.08] p-4 max-h-64 overflow-y-auto text-left"
                  >
                    <div className="space-y-3">
                      {transcript
                        .filter((t) => t.isFinal)
                        .map((entry, i) => (
                          <div
                            key={i}
                            className={`text-sm ${
                              entry.role === "assistant"
                                ? "text-lime-400"
                                : "text-gray-100"
                            }`}
                          >
                            <span className="font-medium">
                              {entry.role === "assistant" ? "AI: " : "You: "}
                            </span>
                            {entry.text}
                          </div>
                        ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-gradient-to-r from-lime-400 to-lime-600 text-[#292929] font-bold hover:opacity-90 shadow-lg shadow-lime-500/20 gap-2" size="lg" asChild>
              <Link to="/pricing">
                <Sparkles className="w-5 h-5" />
                Get This for Your Business
              </Link>
            </Button>
            <Button variant="outline" onClick={onComplete} className="border-white/[0.15] hover:bg-white/[0.05] text-gray-400">
              Close
            </Button>
          </div>

          {remainingDemos !== null && remainingDemos > 0 && (
            <p className="text-xs text-gray-500 mt-6">
              You have {remainingDemos} demo{remainingDemos !== 1 ? "s" : ""} remaining today
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  // Active call view
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-2xl mx-auto py-8"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-100 mb-2">
          Speaking with AI Receptionist
        </h2>
        <p className="text-gray-400">{config.businessName}</p>
        {/* AI Indicator Chip */}
        <div className="mt-3 flex justify-center">
          <AIIndicatorChip isCallActive={status === "active"} />
        </div>
      </div>

      {/* Main call card */}
      <div className="bg-gradient-to-br from-[#2e2e2e] via-[#2d2d2d] to-[#2c2c2c] border border-white/[0.1] p-8 sm:p-12">
        {/* Voice Orb */}
        <div className="flex justify-center mb-8">
          <AnimatedVoiceOrb
            status={status}
            volumeLevel={volumeLevel}
            isMuted={isMuted}
          />
        </div>

        {/* Status */}
        <div className="text-center mb-8">
          <p className="text-lg font-medium text-gray-100 mb-2">
            {getStatusText(status)}
          </p>
          {status === "active" && (
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <Clock className="w-4 h-4 text-lime-400" />
              <span className="font-mono text-lg text-lime-400">{formatDuration(duration)}</span>
              <span className="text-sm text-gray-500">/ 3:00</span>
            </div>
          )}
        </div>

        {/* Time warning */}
        {status === "active" && duration >= 270 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-3 bg-[#333333] border border-white/[0.08] text-center"
          >
            <p className="text-sm text-amber-500 font-medium">
              Less than {300 - duration} seconds remaining
            </p>
          </motion.div>
        )}

        {/* Controls */}
        {(status === "active" || status === "ending") && (
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
              aria-pressed={isMuted}
              className={`w-14 h-14 rounded-full p-0 border-white/[0.15] hover:bg-white/[0.05] ${
                isMuted ? "bg-red-500/10 border-red-500/50" : ""
              }`}
            >
              {isMuted ? (
                <MicOff className="w-6 h-6 text-red-500" />
              ) : (
                <Mic className="w-6 h-6 text-gray-400" />
              )}
            </Button>

            <Button
              size="lg"
              onClick={endDemo}
              className="px-8 gap-2 bg-red-500 hover:bg-red-600 text-white font-bold"
              disabled={status === "ending"}
            >
              <PhoneOff className="w-5 h-5" />
              End Call
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowTranscript(!showTranscript)}
              aria-label={showTranscript ? "Hide transcript" : "Show transcript"}
              aria-pressed={showTranscript}
              className={`w-14 h-14 rounded-full p-0 border-white/[0.15] hover:bg-white/[0.05] ${
                showTranscript ? "bg-lime-500/10 border-lime-500/50" : ""
              }`}
            >
              <MessageSquare className={`w-6 h-6 ${showTranscript ? "text-lime-400" : "text-gray-400"}`} />
            </Button>
          </div>
        )}

        {/* Not active - show loading state */}
        {(status === "generating" || status === "connecting" || status === "requesting-mic") && (
          <div className="text-center">
            <p className="text-sm text-gray-400">
              {status === "requesting-mic" && "Please allow microphone access when prompted"}
              {status === "generating" && "Personalizing your AI experience..."}
              {status === "connecting" && "Almost there..."}
            </p>
          </div>
        )}

        {/* Live Transcript */}
        <AnimatePresence>
          {showTranscript && status === "active" && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              role="log"
              aria-live="polite"
              aria-label="Live conversation transcript"
              className="mt-8 bg-[#333333] border border-white/[0.08] p-4 max-h-48 overflow-y-auto"
            >
              <h4 className="text-sm font-semibold text-gray-100 mb-3">
                Live Transcript
              </h4>
              <div className="space-y-2">
                {transcript.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    Conversation will appear here...
                  </p>
                ) : (
                  transcript
                    .filter((t) => t.isFinal)
                    .slice(-5)
                    .map((entry, i) => (
                      <div
                        key={i}
                        className={`text-sm ${
                          entry.role === "assistant"
                            ? "text-lime-400"
                            : "text-gray-100"
                        }`}
                      >
                        <span className="font-medium">
                          {entry.role === "assistant" ? "AI: " : "You: "}
                        </span>
                        {entry.text}
                      </div>
                    ))
                )}
                <div ref={transcriptEndRef} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tips */}
      {status === "active" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
          className="mt-6 text-center"
        >
          <p className="text-sm text-gray-400">
            <span className="font-medium text-gray-100">Tip:</span> Try asking about pricing,
            availability, or describing a service issue
          </p>
        </motion.div>
      )}

      {/* Back button for non-active states */}
      {(status === "idle" || status === "generating" || status === "connecting") && (
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={onBack} className="text-gray-400 hover:text-gray-100 hover:bg-white/[0.05]">
            Cancel
          </Button>
        </div>
      )}

      {/* Trust Signals */}
      <TrustSignals />
    </motion.div>
  );
}
